import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  X,
  MessageCircle,
  UserPlus,
  UserCheck,
  Mail,
  MapPin,
  Calendar,
  Award,
  Flag,
  Loader2,
  ExternalLink,
  Users,
  Clock
} from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { BUBBLE_COLORS } from "./UserBubble";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

/**
 * UserProfileModal - Shows a user's profile when clicking their name/avatar
 * Features:
 * - View public profile info
 * - Send golf buddy request
 * - Send direct message
 * - View golf stats (if public)
 */
const UserProfileModal = ({ 
  userId, 
  onClose, 
  currentUser,
  onMessageSent 
}) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showMessageInput, setShowMessageInput] = useState(false);
  const [showBuddiesList, setShowBuddiesList] = useState(false);
  const [buddies, setBuddies] = useState([]);
  const [loadingBuddies, setLoadingBuddies] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/users/${userId}/public-profile`);
        setProfile(response.data);
        
        // Pre-fill message with golfer's name
        const firstName = response.data.name?.split(' ')[0] || 'there';
        setMessageText(`Hi ${firstName}!`);
      } catch (error) {
        console.error('Error fetching profile:', error);
        toast.error('Failed to load profile');
        onClose();
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchProfile();
      setShowBuddiesList(false); // Reset when modal opens
    }
  }, [userId, onClose]);

  const fetchBuddies = async () => {
    setLoadingBuddies(true);
    try {
      const response = await axios.get(`${API}/users/${userId}/buddies`);
      setBuddies(response.data.buddies || []);
    } catch (error) {
      console.error("Error fetching buddies:", error);
      toast.error("Failed to load buddies");
    } finally {
      setLoadingBuddies(false);
    }
  };

  const handleShowBuddies = () => {
    setShowBuddiesList(true);
    fetchBuddies();
  };

  const handleBuddyClick = (buddyUserId) => {
    onClose();
    navigate(`/profile/${buddyUserId}`);
  };

  const handleSendGolfBuddyRequest = async () => {
    try {
      setSendingRequest(true);
      await axios.post(`${API}/golf-buddies/request`, {
        target_user_id: userId,
        message: `${currentUser?.name || 'A golfer'} wants to be your golf buddy!`
      });
      
      toast.success('Golf buddy request sent!');
      
      // Update local state
      setProfile(prev => ({
        ...prev,
        pending_request: { from_user_id: currentUser?.user_id, status: 'pending' }
      }));
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to send request';
      if (msg === 'Already golf buddies') {
        toast.info("You're already golf buddies!");
      } else if (msg === 'Request already sent') {
        toast.info('Request already sent');
      } else {
        toast.error(msg);
      }
    } finally {
      setSendingRequest(false);
    }
  };

  const handleAcceptRequest = async () => {
    if (!profile?.pending_request?.request_id) return;
    
    try {
      setSendingRequest(true);
      await axios.post(`${API}/golf-buddies/request/${profile.pending_request.request_id}/accept`);
      toast.success(`You and ${profile.name} are now golf buddies!`);
      setProfile(prev => ({ ...prev, is_golf_buddy: true, pending_request: null }));
    } catch (error) {
      toast.error('Failed to accept request');
    } finally {
      setSendingRequest(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      setSendingMessage(true);
      await axios.post(`${API}/social/messages`, {
        recipient_id: userId,
        content: messageText
      });
      
      toast.success('Message sent!');
      setMessageText('');
      setShowMessageInput(false);
      
      if (onMessageSent) {
        onMessageSent();
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to send message';
      toast.error(msg);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleOpenChat = () => {
    onClose();
    navigate('/chat');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  // Render buddy status / action button
  const renderBuddyAction = () => {
    if (profile?.is_self) {
      return (
        <Button
          variant="outline"
          className="border-[var(--ps-border)] text-[var(--ps-text-secondary)]"
          onClick={() => { onClose(); navigate('/profile'); }}
        >
          Edit Profile
        </Button>
      );
    }

    if (profile?.is_golf_buddy) {
      return (
        <div className="flex items-center gap-2 text-[var(--ps-primary)]">
          <UserCheck className="w-5 h-5" />
          <span className="font-medium">Golf Buddy</span>
        </div>
      );
    }

    if (profile?.pending_request) {
      if (profile.pending_request.from_user_id === currentUser?.user_id) {
        return (
          <Button variant="outline" disabled className="border-amber-500 text-amber-500">
            <Clock className="w-4 h-4 mr-2" />
            Request Pending
          </Button>
        );
      } else {
        return (
          <Button
            onClick={handleAcceptRequest}
            disabled={sendingRequest}
            className="bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)] text-white"
          >
            {sendingRequest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Accept Request
          </Button>
        );
      }
    }

    return (
      <Button
        onClick={handleSendGolfBuddyRequest}
        disabled={sendingRequest}
        className="bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)] text-white"
      >
        {sendingRequest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
        Add Golf Buddy
      </Button>
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-[var(--ps-bg-surface)] rounded-2xl p-8 flex items-center justify-center" onClick={e => e.stopPropagation()}>
          <Loader2 className="w-8 h-8 animate-spin text-[var(--ps-primary)]" />
        </div>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-[var(--ps-bg-surface)] rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header with close button */}
        <div className="relative">
          {/* Banner/Cover */}
          <div className="h-20 bg-gradient-to-r from-green-600 to-green-700" />
          
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors"
            data-testid="close-profile-modal"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Avatar - overlapping banner with bubble color and online glow - CLICKABLE */}
          <div className="absolute -bottom-10 left-1/2 -translate-x-1/2">
            {(() => {
              const colorConfig = BUBBLE_COLORS[profile.bubble_color] || BUBBLE_COLORS.green;
              const baseShadow = '0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3)';
              return (
                <button
                  className="w-20 h-20 rounded-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[var(--ps-bg-surface)] transition-all hover:scale-110 active:scale-95"
                  onClick={() => { onClose(); navigate(`/profile/${profile.user_id}`); }}
                  style={{
                    border: `3px solid ${colorConfig.color}`,
                    boxShadow: profile.is_online 
                      ? `${colorConfig.glow}, ${baseShadow}` 
                      : baseShadow,
                    transition: 'box-shadow 0.3s ease, transform 0.15s ease'
                  }}
                  aria-label={`View ${profile.name}'s full profile`}
                >
                  {profile.picture ? (
                    <img src={profile.picture} alt={profile.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-[var(--ps-primary)] text-white text-2xl font-bold">
                      {profile.name?.charAt(0) || 'G'}
                    </div>
                  )}
                </button>
              );
            })()}
          </div>
        </div>

        {/* Profile Info */}
        <div className="pt-12 px-4 pb-4">
          {/* Name & Username */}
          <div className="text-center mb-4">
            <h2 className="text-xl font-bold text-[var(--ps-text-primary)]">{profile.name}</h2>
            {profile.username && (
              <p className="text-[var(--ps-text-muted)]">@{profile.username}</p>
            )}
          </div>

          {/* Buddy Status / Action */}
          <div className="flex justify-center mb-4">
            {renderBuddyAction()}
          </div>

          {/* Bio */}
          {profile.bio && (
            <p className="text-center text-[var(--ps-text-secondary)] text-sm mb-4">
              {profile.bio}
            </p>
          )}

          {/* Quick Info */}
          <div className="flex justify-center gap-6 text-sm text-[var(--ps-text-muted)] mb-4">
            {profile.location && (
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                <span>{profile.location}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>Joined {formatDate(profile.created_at)}</span>
            </div>
          </div>

          {/* Golf Stats (if available) */}
          {profile.stats && (
            <div className="bg-[var(--ps-bg-elevated)] rounded-xl p-4 mb-4">
              <h3 className="text-sm font-semibold text-[var(--ps-text-primary)] mb-3 flex items-center gap-2">
                <Award className="w-4 h-4 text-[var(--ps-primary)]" />
                Golf Stats
              </h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-[var(--ps-primary)]">
                    {profile.stats.rounds_played || 0}
                  </p>
                  <p className="text-xs text-[var(--ps-text-muted)]">Rounds</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-[var(--ps-text-primary)]">
                    {typeof profile.stats.handicap === 'number' && Number.isFinite(profile.stats.handicap) ? profile.stats.handicap.toFixed(1) : 'N/A'}
                  </p>
                  <p className="text-xs text-[var(--ps-text-muted)]">Handicap</p>
                </div>
                <button onClick={handleShowBuddies} className="hover:opacity-80 transition-opacity">
                  <p className="text-2xl font-bold text-[var(--ps-text-primary)]">
                    {profile.buddies_count ?? 0}
                  </p>
                  <p className="text-xs text-[var(--ps-text-muted)] underline">Buddies</p>
                </button>
              </div>
            </div>
          )}

          {/* Buddies List - shows when Buddies count is clicked */}
          {showBuddiesList && (
            <div className="bg-[var(--ps-bg-elevated)] rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-[var(--ps-text-primary)] flex items-center gap-2">
                  <Users className="w-4 h-4 text-[var(--ps-primary)]" />
                  {profile.name}'s Golf Buddies
                </h3>
                <button onClick={() => setShowBuddiesList(false)} className="text-[var(--ps-text-muted)] hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              {loadingBuddies ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-[var(--ps-primary)]" />
                </div>
              ) : buddies.length === 0 ? (
                <p className="text-center text-[var(--ps-text-muted)] text-sm py-2">No golf buddies yet</p>
              ) : (
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {buddies.map((buddy) => (
                    <button
                      key={buddy.user_id}
                      onClick={() => handleBuddyClick(buddy.user_id)}
                      className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--ps-bg-hover)] transition-colors text-left"
                    >
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={buddy.picture} />
                        <AvatarFallback className="bg-[var(--ps-primary)] text-white text-xs">
                          {buddy.name?.charAt(0) || 'G'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--ps-text-primary)] truncate">{buddy.name}</p>
                        {buddy.handicap !== null && buddy.handicap !== undefined && (
                          <p className="text-xs text-[var(--ps-primary)]">{buddy.handicap} HCP</p>
                        )}
                      </div>
                      {buddy.is_following && (
                        <span className="text-[10px] text-[var(--ps-primary)] bg-[var(--ps-primary)]/20 px-1.5 py-0.5 rounded">
                          Mutual
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Private Profile Notice */}
          {!profile.stats && profile.profile_privacy === 'private' && !profile.is_golf_buddy && !profile.is_self && (
            <div className="bg-[var(--ps-bg-elevated)] rounded-xl p-4 mb-4 text-center">
              <Flag className="w-6 h-6 text-[var(--ps-text-muted)] mx-auto mb-2" />
              <p className="text-sm text-[var(--ps-text-muted)]">
                This golfer's profile is private. Become golf buddies to see more!
              </p>
            </div>
          )}

          {/* Action Buttons */}
          {!profile.is_self && (
            <div className="space-y-3">
              {/* Message Section */}
              {showMessageInput ? (
                <div className="bg-[var(--ps-bg-elevated)] rounded-xl p-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-lg px-3 py-2 text-[var(--ps-text-primary)] text-sm"
                      data-testid="profile-message-input"
                      onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={sendingMessage || !messageText.trim()}
                      className="bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)] text-white px-4"
                      data-testid="profile-send-message-btn"
                    >
                      {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send'}
                    </Button>
                  </div>
                  <button
                    onClick={() => setShowMessageInput(false)}
                    className="text-xs text-[var(--ps-text-muted)] mt-2 hover:underline"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1 border-[var(--ps-border)] text-[var(--ps-text-primary)]"
                    onClick={() => setShowMessageInput(true)}
                    data-testid="start-message-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Message
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 border-[var(--ps-border)] text-[var(--ps-text-primary)]"
                    onClick={handleOpenChat}
                    data-testid="view-chat-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Chat
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;
